import { createContext, useContext, useMemo, useRef } from "react";
import { GraphQLClient } from "graphql-request";

import { createAuthedClient } from "lib/hooks/api/graphQlClientFactory";

// Default to an anonymous client so consumers rendered outside a provider (e.g. isolated hook
// tests, or trees that never mount AppTokenProvider) still get a usable client — mirroring how
// AppTokenContext defaults to "". In the real app, GraphQlClientProvider always supplies the
// token-authed client; the default only sends unauthenticated requests, which is the correct
// fallback for guests.
const GraphQlClientContext = createContext<GraphQLClient>(createAuthedClient(""));

/**
 * Publishes one shared GraphQL client, built once and never rebuilt on a token refresh — it reads
 * the token from a ref per request. Rebuilding would strand the dead token in anything holding the
 * old client, such as a cached query's `queryFn`.
 */
export function GraphQlClientProvider({
  token,
  children,
}: {
  token: string;
  children: React.ReactNode;
}) {
  const tokenRef = useRef(token);
  // Not in an effect: child effects run before parents, so a child fetching there would send the
  // stale token. Safe during render — it is only read later, when a request builds its headers.
  tokenRef.current = token;

  const client = useMemo(() => createAuthedClient(() => tokenRef.current), []);
  return <GraphQlClientContext.Provider value={client}>{children}</GraphQlClientContext.Provider>;
}

export function useProvidedGraphQlClient(): GraphQLClient {
  return useContext(GraphQlClientContext);
}
